import { ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Keycloak from 'keycloak-js';
import deukaLogo from '../../../assets/deuka.svg';
import { routes } from '../../../store/Workflow.store';
import { routes as sharedRoutes } from '../../../shared/constants';
import { slide as Menu, State } from 'react-burger-menu';
import iconBurger from '../../../assets/icon-burger.svg';
import IconButton, { IconSize } from '../IconButton/IconButton';
import { Link, useLocation } from 'react-router-dom';
import { NavigationGroup, generateNavigationEntries } from '../../../store/Navigation.store';
import { useDispatch, useSelector } from 'react-redux';
import Footer, { FOOTER_LINKS } from '../Footer/Footer';
import CookieBanner from '../CookieBanner/CookieBanner';
import EnvironmentBanner from '../EnvironmentBanner/EnvironmentBanner';
import { ALLOW_COOKIES_KEY } from '../../../localStorage';
import FeatureToggle from '../FeatureToggle/FeatureToggle';
import AddToHomescreenBanner from '../AddToHomescreenBanner/AddToHomescreenBanner';
import type { RootState } from '../../../configureStore';
import {
    BestelluebersichtSelectors,
    acknowledgeAuftragskorb,
    fetchAuftragskorbHasNew,
    getBestellungen,
} from '../../../store/Bestelluebersicht.store';
import { Order } from '../../../shared/types/enums';
import styles from './Shell.module.scss';
import './burgermenu.scss';
import { Breakpoint, useBreakpoint } from '../../hooks/useBreakpoint';
import { CurrentUser } from '../../types';
// eslint-disable-next-line import/namespace
import { Account } from './components/Account';
import { NachrichtenBell } from './components/NachrichtenBell';
import FlutterBridge from '../../../flutter/flutterBridge';
import Config from '../../../shared/config';
import { getData } from '../../../shared/fetchApi';
import { useTracking } from '../../hooks/useTracking';
import { SET_CURRENT_USER } from '../../../store/keycloak.store';

const { isApp } = Config.getConfig();

export type Props = Readonly<{
    children: ReactNode;
    keycloak: Partial<Keycloak>;
    currentUser: CurrentUser;
}>;

const Shell = (props: Props): ReactElement => {
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    useTracking();
    const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
    const unreadBadgeSuppressedUntilSyncRef = useRef(false);
    const location = useLocation();
    const menuEntries: NavigationGroup[] = useSelector((rootState: RootState): NavigationGroup[] => rootState.navigation.navigationGroups);
    const [cookieBannerVisible, setCookieBannerVisible] = useState(localStorage.getItem(ALLOW_COOKIES_KEY) === null);
    const isDesktop = useBreakpoint(Breakpoint.DESKTOP);

    const dispatch = useDispatch();
    const { benutzerRollen, hasSilos, isAdmin } = useSelector((state: RootState) => state.navigation);
    const hasNewAuftraege = useSelector((state: RootState) => BestelluebersichtSelectors.hasNewAuftraege(state));

    useEffect(() => {
        dispatch(generateNavigationEntries(benutzerRollen));
    }, [dispatch, benutzerRollen, hasSilos]);

    dispatch({ type: SET_CURRENT_USER, user: { ...props.currentUser, email: props.keycloak.tokenParsed?.email } });

    const checkUnreadMessages = useCallback(async () => {
        if (isAdmin) {
            setHasUnreadMessages(false);
            return;
        }
        try {
            const response = await getData<boolean>('nachrichten/unread');
            console.log('Antwort von /nachrichten/unread:', response);
            if (unreadBadgeSuppressedUntilSyncRef.current) {
                setHasUnreadMessages(false);
                if (!response) {
                    unreadBadgeSuppressedUntilSyncRef.current = false;
                }
            } else {
                setHasUnreadMessages(response);
            }
        } catch (error) {
            console.error('Fehler beim Abrufen der ungelesenen Nachrichten:', error);
        }
    }, [isAdmin]);

    const onNachrichtenBellMarkReadOptimistic = useCallback((): void => {
        unreadBadgeSuppressedUntilSyncRef.current = true;
        setHasUnreadMessages(false);
    }, []);

    useEffect(() => {
        checkUnreadMessages();
    }, [checkUnreadMessages]);

    const openMenu = (): void => {
        setMenuIsOpen(true);
        checkUnreadMessages();
        dispatch({ type: fetchAuftragskorbHasNew });
        const isOnBestelluebersicht =
            location.pathname === sharedRoutes.bestelluebersicht || location.pathname === sharedRoutes.auftragsuebersicht;
        if (!isOnBestelluebersicht) {
            dispatch(
                getBestellungen({
                    page: 1,
                    size: 10,
                    sortColumn: 'bestelldatum',
                    sortOrder: Order.DESC,
                    filter: { isDraft: true, geschlossen: false },
                    includeBestellpositionen: false,
                })
            );
        }
    };

    const closeMenu = (): void => {
        setMenuIsOpen(false);
    };

    const setMenuOpen = (state: State): void => {
        setMenuIsOpen(state.isOpen);
    };

    const showCookieBanner = (): void => {
        setCookieBannerVisible(true);
    };

    const hideCookieBanner = (): void => {
        setCookieBannerVisible(false);
    };

    return (
        <>
            <FeatureToggle featureName="PROGRESSIVE_WEB_APP">
                <AddToHomescreenBanner />
            </FeatureToggle>
            <Menu
                right
                outerContainerId="root"
                pageWrapId="page-container"
                isOpen={menuIsOpen}
                onStateChange={setMenuOpen}
                customCrossIcon={false}
                width={isDesktop ? 300 : '80%'}
            >
                <Account
                    username={props.currentUser.name}
                    keycloak={props.keycloak}
                    setMenuIsOpen={setMenuIsOpen}
                    hasUnreadMessages={hasUnreadMessages}
                    onNachrichtenBellMarkReadOptimistic={onNachrichtenBellMarkReadOptimistic}
                    showNachrichtenBell={!isDesktop}
                />
                <hr className={styles._divider} />
                {menuEntries.map((group, groupIndex) => (
                    <div key={groupIndex} className={styles._menuItemGroup}>
                        <label className={styles._menuLabel}>{group.groupLabel}</label>
                        {group.entries.map((entry, entryIndex) => {
                            const showNotification = hasNewAuftraege && entry.name === routes.auftragsuebersicht;

                            const className =
                                location.pathname === entry.route && entry.route !== routes.underConstruction
                                    ? [styles._menuItem, styles['_menuItem--active']].join(' ')
                                    : styles._menuItem;

                            if (entry.isExternalLink) {
                                return (
                                    <a
                                        data-cy={`navigation${entry.name}`}
                                        key={entryIndex}
                                        onClick={() => {
                                            window.umami?.track('sidebar-navigation-click', {
                                                entryName: entry.name,
                                            });
                                            closeMenu();
                                        }}
                                        href={entry.route}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={className}
                                    >
                                        {entry.label}
                                    </a>
                                );
                            }

                            return (
                                <div key={entryIndex} style={{ position: 'relative' }}>
                                    <Link
                                        data-cy={`navigation${entry.name}`}
                                        onClick={() => {
                                            if (entry.name === routes.auftragsuebersicht) {
                                                dispatch({ type: acknowledgeAuftragskorb });
                                            }
                                            window.umami?.track('sidebar-navigation-click', {
                                                entryName: entry.name,
                                            });
                                            closeMenu();
                                        }}
                                        to={entry.route}
                                        className={className}
                                    >
                                        {entry.label}
                                    </Link>
                                    {showNotification && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: '14px',
                                                right: '12px',
                                                width: '10px',
                                                height: '10px',
                                                backgroundColor: '#ff0000',
                                                borderRadius: '50%',
                                            }}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
                {isDesktop ? null : (
                    <div key={'footer-links'} className={styles._menuItemGroup}>
                        <label className={styles._menuLabel}>Hinweise</label>
                        {[
                            ...FOOTER_LINKS.map((entry, index) => {
                                return (
                                    <a
                                        key={index}
                                        onClick={() => {
                                            window.umami?.track('sidebar-navigation-click', {
                                                entryName: entry.label,
                                            });
                                            closeMenu();
                                        }}
                                        href={entry.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles._menuItem}
                                    >
                                        {entry.label}
                                    </a>
                                );
                            }),
                            <a
                                key={'footer_menu_entry_cookie_settings'}
                                target="_blank"
                                onClick={() => {
                                    window.umami?.track('sidebar-navigation-click', {
                                        entryName: 'cookie-einstellungen',
                                    });
                                    closeMenu();
                                    showCookieBanner();
                                }}
                                className={styles._menuItem}
                            >
                                Cookie-Einstellungen
                            </a>,
                            isApp() ? (
                                <a
                                    href={routes.lizenzen}
                                    key={'footer_menu_entry_licenses'}
                                    onClick={() => {
                                        window.umami?.track('sidebar-navigation-click', {
                                            entryName: 'lizenzen',
                                        });
                                        FlutterBridge.sendLicenses();
                                    }}
                                    className={styles._menuItem}
                                >
                                    Lizenzen
                                </a>
                            ) : null,
                        ]}
                    </div>
                )}
            </Menu>
            <div id="page-container" className={styles._container}>
                <EnvironmentBanner />
                <div className={styles._mainBackground} />
                <div className={styles._headerBackground} />
                <header className={styles._header}>
                    <div className={styles._headerContent}>
                        <div className={styles._deukaContainer}>
                            <Link to={'/'}>
                                <img src={deukaLogo} className={styles._deukalogo} alt="Deuka logo" />
                            </Link>
                        </div>
                    </div>
                    <div className={styles._headerActions}>
                        {isDesktop && !isAdmin ? (
                            <NachrichtenBell
                                hasUnreadMessages={hasUnreadMessages}
                                onMarkReadOptimistic={onNachrichtenBellMarkReadOptimistic}
                                className={styles._headerBell}
                            />
                        ) : null}
                        <IconButton burger={true} icon={iconBurger} iconSize={IconSize.LARGE} alt="Navigation öffnen" onClick={openMenu} />
                    </div>
                </header>
                <div className={styles._stickyFix}></div>
                <main className={styles._main}>{props.children}</main>
                {[routes.npRechner, routes.fragenUndAntworten].indexOf(location.pathname) === -1 ? (
                    <div className={styles._footer}>
                        <Footer onShowCookieBanner={showCookieBanner} />
                    </div>
                ) : null}
                <CookieBanner onHideCookieBanner={hideCookieBanner} bannerVisible={cookieBannerVisible} />
            </div>
        </>
    );
};

export default Shell;
